import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, filter, map, mergeMap, Observable, tap } from 'rxjs';
import { VehicleMake } from '../../../shared/interfaces/vehicle-make';
import { VehicleModel } from '../../../shared/interfaces/vehicle-model';
import { VehicleTrim } from '../../../shared/interfaces/vehicle-trim';
import { VehicleMakesService } from '../../../vehicles/services/vehicle-makes.service';
import { VehicleModelsService } from '../../../vehicles/services/vehicle-models.service';
import { VehicleTrimsService } from '../../../vehicles/services/vehicle-trims.service';
import { CatalogsService } from '../../../admin/services/catalogs.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Paginator } from '../../../shared/interfaces/paginator';
import { Product } from '../../../shared/interfaces/product';
import { ActivatedRoute, Data } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { PagerService } from '../../../admin/services/pager.service';
import { Category } from '../../../shared/interfaces/category';
import { CategoryFilterComponent } from '../../components/category-filter/category-filter.component';

type FormType = {
    make_id: FormControl<string | number>;
    model_id: FormControl<string | number>;
    trim_id: FormControl<string | number>;
};

@Component({
    selector: 'app-catalog',
    templateUrl: './catalog.component.html',
    styleUrl: './catalog.component.scss',
})
export class CatalogComponent implements OnInit, AfterViewInit {

    public catalogId!: string | number;

    public categories$: Observable<Category[]>;

    @ViewChild(CategoryFilterComponent) public categoryFilter: CategoryFilterComponent | undefined;

    public fixMake = false;

    public form: FormGroup<FormType> = this.forms.group({
        make_id: ['', [Validators.required]],
        model_id: ['', [Validators.required]],
        trim_id: [''],
    }) as FormGroup<FormType>;

    public makes$: Observable<VehicleMake[]> | undefined;

    public models$: Observable<VehicleModel[]> | undefined;

    @ViewChild(MatPaginator) public paginator: MatPaginator | undefined;

    public products$: Observable<Product[]> = this.route.data.pipe(
        map((d) => d['products'] as Paginator<Product>),
        tap((page) => this.total = page?.total),
        map((page) => page?.data),
    );

    @ViewChild('search') public search: ElementRef<HTMLInputElement> | undefined;

    public selectedCategory = '';

    public total = -1;

    public trims$: Observable<VehicleTrim[]> | undefined;

    private readonly _categories = new BehaviorSubject<Category[]>([]);

    private categoryNeedsReset = false;

    public constructor(
        private readonly makes: VehicleMakesService,
        private readonly models: VehicleModelsService,
        private readonly trims: VehicleTrimsService,
        private readonly forms: FormBuilder,
        private readonly catalogs: CatalogsService,
        private readonly destroyRef: DestroyRef,
        private readonly route: ActivatedRoute,
        private readonly pager: PagerService,
    ) {
        this.categories$ = this._categories.asObservable();
    }

    public ngAfterViewInit(): void {
        this.catalogId = this.route.snapshot.paramMap.get('catalog') || '';
        const vehicleMakeId = this.route.snapshot.queryParamMap.get('vehicleMakeId');
        if (vehicleMakeId) {
            this.fixMake = true;
            this.form.controls.make_id.setValue(parseInt(vehicleMakeId));
        }

        this.pager.init(this.search, this.paginator, undefined, () => this.refresh(), this.destroyRef);
    }

    public ngOnInit(): void {
        this.initMakes();
        this.initModels();
        this.initTrims();

        this.trackCategoryReset();
    }

    public onCategorySelected(name: string | number): void {
        this.selectedCategory = name as string;
        this.paginator!.pageIndex = 0;
        this.refresh();
    }

    public onSearch(): void {
        if (!this.catalogId) {
            throw new Error('Catalog Id Must be provided');
        }

        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        this.refresh();
    }

    private initMakes(): void {
        this.makes$ = this.makes.page(1, 999999).pipe(
            takeUntilDestroyed(this.destroyRef),
            map((page) => page.data),
        );
    }

    private initModels(): void {
        this.models$ = this.form.controls.make_id.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(v => !!v),
            tap(() => this.form.get('model_id')!.setValue('')),
            mergeMap((makeId) => this.models.forMake(makeId, 1, 999999, '', { sortBy: 'year' })),
            map((page) => page.data),
        );
    }

    private initTrims(): void {
        this.trims$ = this.form.controls.model_id.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(v => !!v),
            tap(() => this.form.get('trim_id')!.setValue('')),
            mergeMap((modelId) => this.trims.forModel(modelId, 1, 999999)),
            map((page) => page.data),
            tap((trims) => {
                const trimControl = this.form.get('trim_id');
                if (!trimControl) { return; }

                if (trims.length > 0) {
                    trimControl.addValidators(Validators.required);
                } else {
                    trimControl.removeValidators(Validators.required);
                }
                trimControl.updateValueAndValidity({ emitEvent: false });
            }),
        );
    }

    private refresh(): void {
        if (this.categoryNeedsReset) {
            this.selectedCategory = '';
            this.categoryNeedsReset = false;
        }

        this.catalogs.products(
            this.catalogId,
            this.form.controls.make_id.value,
            this.form.controls.model_id.value,
            this.form.controls.trim_id.value,
            (this.paginator?.pageIndex || 0) + 1,
            this.paginator?.pageSize,
            this.search?.nativeElement.value,
            { category: this.selectedCategory || '' },
        ).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((page) => {
            /* eslint-disable @typescript-eslint/no-explicit-any */
            this._categories.next((page as any).meta?.['category_counts']);
            (this.route.data as BehaviorSubject<Data>).next({ products: page });
        });
    }

    private trackCategoryReset(): void {
        const controlsToWatch = [
            this.form.controls.make_id,
            this.form.controls.model_id,
            this.form.controls.trim_id,
        ];
        controlsToWatch.forEach(control => {
            control.valueChanges.pipe(
                takeUntilDestroyed(this.destroyRef),
            ).subscribe(() => {
                this.categoryNeedsReset = true;
            });
        });
    }
}
